import * as React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getImageUrl } from '@Components/forms/inputs/ImageInput';
import { HeaderBackgrounds } from '@Components/Header';
import Icon, { IconSizes, sizes } from '@Components/Icon';
import Container from '@Components/layout/Container';
import Layout, { LayoutBackgrounds } from '@Components/layout/Layout';
import Row from '@Components/layout/Row';
import Paper, { PaperWidth } from '@Components/Paper';
import PaperBody from '@Components/PaperBody';
import { RouteConfig } from '@Config/routes';
import { useRouteParams } from '@Hooks/useRouteParams';
import AuthenticationForm from '@Routes/auth/AuthenticationPage/AuthenticationForm';
import { getMainLogo } from '@Store/app/app.selectors';
import { LoginFormData } from '@Store/auth/auth.types';
import useReferer from '@Utils/hooks/useReferer';

import styles from './AuthenticationPage.module.scss';
import { RegisterFormData } from './RegisterForm/RegisterForm';

enum AuthTabTypes {
  login = 'login',
  register = 'register',
}

const authTabs = [AuthTabTypes.login, AuthTabTypes.register];

const AuthenticationPage: React.FunctionComponent<{}> = () => {
  const { authType } = useRouteParams(RouteConfig.Authentication);
  const navigate = useNavigate();

  const [referer] = useReferer();

  const registerInitialValues: RegisterFormData = {
    username: '',
    email: '',
    password: '',
    conditionsAccepted: false,
    receiveMarketingMaterial: true,
    addNaturalCompany: true,
  };

  const loginInitialValues: LoginFormData = {
    email: '',
    password: '',
  };

  const onTabChange = (activeTab: number) => {
    navigate({ pathname: RouteConfig.Authentication.buildLink({ authType: authTabs[activeTab] }) }, { replace: true });
  };

  const mainLogo = useSelector(getMainLogo);
  const mainLogoUrl = mainLogo && getImageUrl(mainLogo);

  const initialTab = authTabs.indexOf(authType as AuthTabTypes);

  return (
    <Layout background={LayoutBackgrounds.authPageBackground} headerBackground={HeaderBackgrounds.white}>
      <Container>
        <Row alignCenter justifyCenter>
          <Paper marginBottom width={PaperWidth.m}>
            <PaperBody>
              <Row constant justifyCenter className={styles.authPageLogo}>
                {mainLogoUrl ? (
                  <img src={mainLogoUrl} alt="logo" {...sizes[IconSizes.logoBig]} />
                ) : (
                  <Icon size={IconSizes.logoBig} icon="green-sejico-logo" />
                )}
              </Row>
              <AuthenticationForm
                referer={referer}
                registerInitialValues={registerInitialValues}
                loginInitialValues={loginInitialValues}
                onTabChange={onTabChange}
                initialTab={initialTab < 0 ? 0 : initialTab}
              />
            </PaperBody>
          </Paper>
        </Row>
      </Container>
    </Layout>
  );
};

export default AuthenticationPage;
